/**
 * API client for the AI that answers calls on its own ("AI-vastaja", Settings
 * -> Phone -> AI answering), and the browser's mirror of its settings group.
 *
 * Its own file rather than more of `telephony.ts`, which is at the size limit
 * AGENTS.md section 4 sets. The settings group still travels inside the one
 * telephony row (GET/PUT /admin/crm/calls/settings), so `telephony.ts` imports
 * the status and patch types from here; the knowledge text and the AI's call
 * history are endpoints of their own and live only here.
 *
 * A HAND-WRITTEN MIRROR of `apps/api/src/modules/crm-calls/lib/agent/config.ts`,
 * `readiness.ts`, `types.ts`, `knowledge.ts` and `recent.ts`. `aiAgentNotes.test.ts` (in the
 * settings folder) reads those files and holds the lists and bounds below
 * against them, so a value added on the server fails a test here instead of
 * rendering as a raw key or a form the server answers with 400.
 */
import { ApiError, api } from "./api";
import type { CallDirection } from "./callTypes";

/** When the AI takes an ordinary caller. Mirrors `AI_AGENT_MODES`. */
export const AI_AGENT_MODES = ["afterHours", "noAnswer"] as const;
export type AiAgentMode = (typeof AI_AGENT_MODES)[number];

/** Where a caller who asks for a person goes. Mirrors `AI_AGENT_HANDOFF`. */
export const AI_AGENT_HANDOFF = ["operators", "voicemail"] as const;
export type AiAgentHandoff = (typeof AI_AGENT_HANDOFF)[number];

/**
 * Where in the call the AI took over, as stored on the row. Mirrors
 * `AI_AGENT_ENTRIES`, in its order. "outbound" is the AI placing the call
 * itself; its settings live in `aiOutbound.ts`, but its calls are listed with
 * the rest here.
 */
export const AI_AGENT_ENTRIES = ["first", "afterHours", "noAnswer", "outbound"] as const;
export type AiAgentEntry = (typeof AI_AGENT_ENTRIES)[number];

/**
 * Everything that stops the AI answering. Mirrors `AGENT_BLOCKERS`, in its
 * order, which is the order somebody would fix them in.
 */
export const AGENT_BLOCKERS = [
  "disabled",
  "no_inbound",
  "no_public_url",
  "no_elevenlabs_key",
  "no_voice",
  "no_gemini_key",
  "no_greeting",
  "no_mode",
] as const;
export type AgentBlocker = (typeof AGENT_BLOCKERS)[number];

/** How the AI part of a call ended. Mirrors `AGENT_OUTCOMES`. */
export const AGENT_OUTCOMES = ["handoff", "voicemail", "goodbye", "hangup", "timeout", "error"] as const;
export type AgentOutcome = (typeof AGENT_OUTCOMES)[number];

/**
 * The key the summary counts a call with no outcome under: the AI is still on
 * it, or the process died before the end was written. Mirrors
 * `UNFINISHED_OUTCOME`.
 */
export const AGENT_UNFINISHED_OUTCOME = "unfinished";

/** A marker at the start of the model's answer that steered the call. */
export type AgentControl = "handoff" | "end";

/** The bounds `clampAiAgent` holds the two timings inside. */
export const AGENT_SILENCE_MS = { min: 250, max: 1500, fallback: 500 } as const;
export const AGENT_MAX_CALL_SEC = { min: 60, max: 1200, fallback: 300 } as const;
/** Ceilings the PUT schema refuses past, rather than clamps to. */
export const AGENT_MAX_TEST_CALLERS = 20;
export const AGENT_GREETING_MAX = 500;
export const AGENT_MODEL_MAX = 100;
/** The knowledge text's ceiling in characters. Mirrors `AGENT_KNOWLEDGE_MAX`. */
export const AGENT_KNOWLEDGE_MAX = 80000;
/**
 * Characters per token the server estimates Estonian at. Mirrors
 * `ET_CHARS_PER_TOKEN`, so the editor's count for unsaved text is the number
 * the server reports once it is saved.
 */
export const AGENT_CHARS_PER_TOKEN = 2.4;

/**
 * Model ids the field suggests, fastest first as measured from the prod server
 * on 2026-09-10 (docs/crm-calls-ai-uuring.md). Suggestions rather than a
 * list: Gemini ships a new Flash about monthly, and the field has to take it
 * the day it appears.
 */
export const AGENT_MODEL_SUGGESTIONS = ["gemini-3.5-flash-lite", "gemini-3.1-flash-lite", "gemini-3.5-flash"] as const;

/** The stored group. Mirrors `TelephonyAiAgent`; it holds no secrets. */
export interface TelephonyAiAgent {
  /** Master switch. Ships off. */
  enabled: boolean;
  /** When the AI takes an ordinary caller. Ignored while `testCallers` is non-empty. */
  modes: AiAgentMode[];
  /** E.164 numbers the AI answers first. Non-empty = test mode, nobody else is touched. */
  testCallers: string[];
  /** A voice reference (`elevenlabs:<id>`) or a bare ElevenLabs id. */
  voice: string;
  /** Gemini model id. */
  model: string;
  /** First sentence the caller hears; carries the legal AI disclosure. */
  greeting: string;
  silenceMs: number;
  maxCallSec: number;
  /** Where a test caller who asks for a person goes. */
  handoff: AiAgentHandoff;
}

/** The group as the settings GET returns it: stored values plus the server's verdict. */
export interface TelephonyAiAgentStatus extends TelephonyAiAgent {
  /** Switched on and nothing missing: the AI would really pick up. */
  ready: boolean;
  /** Everything missing, all at once. `aiAgentNotes.ts` decides how each is said. */
  blockers: AgentBlocker[];
}

/**
 * The PUT fragment. Flat, and the two lists are sent whole: a form that posts
 * two numbers means there are two now, the rule every list in this row follows.
 */
export type TelephonyAiAgentPatch = Partial<TelephonyAiAgent>;

// ---------------------------------------------------------------- knowledge

export interface AgentKnowledge {
  text: string;
  savedAt: string | null;
  /** User id of whoever saved it last. */
  savedBy: string | null;
  /** That person's name, resolved by the server; null when the lookup found nobody. */
  savedByName: string | null;
  chars: number;
  /** The server's estimate for `text`; the editor extends its ratio to unsaved edits. */
  approxTokens: number;
}

export interface AgentKnowledgeDraft {
  text: string;
  /** What the owner has to check before this becomes something the AI says. */
  notes: string[];
  chars: number;
  approxTokens: number;
}

export function getAgentKnowledge(): Promise<AgentKnowledge> {
  return api.get("/admin/crm/calls/agent/knowledge");
}

export function saveAgentKnowledge(text: string): Promise<AgentKnowledge> {
  return api.put("/admin/crm/calls/agent/knowledge", { text });
}

/**
 * A prepared starting text. Fetching it saves nothing: the editor puts it in
 * the box and the owner decides.
 */
export function getAgentKnowledgeDraft(): Promise<AgentKnowledgeDraft> {
  return api.get("/admin/crm/calls/agent/knowledge/draft");
}

/**
 * The ceiling from a 400 `too_long`, or null for any other failure - so the
 * editor can say "too long, the limit is X" instead of a bare "could not save".
 */
export function knowledgeTooLongMax(err: unknown): number | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const body = err.body as { error?: unknown; max?: unknown } | null;
  if (!body || body.error !== "too_long") return null;
  return typeof body.max === "number" ? body.max : AGENT_KNOWLEDGE_MAX;
}

// ---------------------------------------------------------------- recent calls

/** One caller turn and the AI's answer to it. Mirrors `AgentTurnLog`. */
export interface AgentTurn {
  heard: string;
  said: string;
  speechEndMs: number;
  sttMs: number | null;
  llmFirstSentenceMs: number | null;
  /** Caller stopped talking -> first AI audio left our server. THE number. */
  firstAudioMs: number | null;
  bargedIn: boolean;
  /** The voice stopped before it had said `said` to the end: the callee heard less than this. */
  cutShort?: boolean;
  control: AgentControl | null;
}

/**
 * One call the AI took part in.
 *
 * The nullable fields are nullable on purpose: a call whose AI part is still
 * running, or whose end was never written, has no outcome and no end yet, and
 * the list must still draw it.
 *
 * `direction` and `toNumber` are there so the list can name the other person
 * with `callCounterpart`: on a call the AI placed, `fromNumber` is our own line.
 */
export interface AgentCallRow {
  id: string;
  startedAt: string;
  direction: CallDirection;
  toNumber: string;
  fromNumber: string | null;
  /** A call the AI placed to a test number instead of the company. */
  agentTest: boolean;
  companyName: string | null;
  agentEntry: AiAgentEntry | null;
  agentOutcome: AgentOutcome | null;
  agentStartedAt: string | null;
  agentEndedAt: string | null;
  turns: AgentTurn[];
  tokensIn: number | null;
  tokensOut: number | null;
  ttsChars: number | null;
}

/** First-audio latency over the listed calls, in milliseconds. Null when `n` is 0. */
export interface AgentLatencyStats {
  n: number;
  mean: number | null;
  median: number | null;
  p90: number | null;
  min: number | null;
  max: number | null;
}

export interface AgentCallsSummary {
  calls: number;
  turns: number;
  firstAudio: AgentLatencyStats;
  bargeIns: number;
  /**
   * Calls per outcome, a call with none under `AGENT_UNFINISHED_OUTCOME`. Keyed
   * by string: a newer server may send one this build does not name.
   */
  outcomes: Partial<Record<string, number>>;
  /** Calls per entry point ("first" is a test caller, "outbound" a call the AI placed). */
  entries: Partial<Record<string, number>>;
}

export interface AgentRecentCalls {
  calls: AgentCallRow[];
  summary: AgentCallsSummary;
}

export function listRecentAgentCalls(limit = 20): Promise<AgentRecentCalls> {
  return api.get(`/admin/crm/calls/agent/recent?limit=${encodeURIComponent(String(limit))}`);
}
